// src/pages/teaching/Teaching_Master_Add_Edit_Screen.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Row,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useVendorBranch from '../../utils/useVendorBranch';
import Select from 'react-select';

const Teaching_Master_Add_Edit_Screen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hydratingRef = useRef(false);

  const { user } = useSelector((state) => state.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});

  const [batchName, setBatchName] = useState('');
  const [subjectName, setSubjectName] = useState('');

  const [chapterOptions, setChapterOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);

  // ✅ Vendor/Branch separate state (VendorBranchSelector safe)
  const [selectionData, setSelectionData] = useState({
    vendor_id: user?.vendor_id || 0,
    branch_id: user?.branch_id || 0,
  });

  const [teachingData, setTeachingData] = useState({
    id: 0,

    vendor_id: user?.vendor_id || 0,
    branch_id: user?.branch_id || 0,

    class_id: '',
    batch_id: '',
    faculty_id: '',

    subject_id: '',
    chapter_id: '',
    topic_id: '',

    date: new Date().toISOString().substring(0, 10),
    timetable_id: '',
    topic_covered: '',
    homework: '',
  });

  // ✅ FIXED unwrap: sirf axios response me res.data return kare
  const unwrap = (res) => {
    if (!res) return res;

    // axios response usually has numeric status + data
    if (typeof res?.status === 'number' && res?.data !== undefined) {
      return res.data;
    }

    // otherwise it's already your API body (has data array inside)
    return res;
  };

  // ✅ converts "" -> null, "12" -> 12
  const toNumOrNull = (v) => {
    if (v === '' || v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // ✅ Sync vendor/branch into teachingData (only these fields)
  useEffect(() => {
    setTeachingData((p) => ({
      ...p,
      vendor_id: selectionData.vendor_id || 0,
      branch_id: selectionData.branch_id || 0,
    }));
  }, [selectionData.vendor_id, selectionData.branch_id]);

  // ✅ CHAPTER API (uses chapterTopicList => [{id,text}])
  const GetChaptersBySubjectId = async (subjectId) => {
    try {
      if (!subjectId) {
        setChapterOptions([]);
        setTopicOptions([]);
        return;
      }

      const vendorId = selectionData.vendor_id || 0;
      const branchId = selectionData.branch_id || 0;

      const res = await apiService.get(
        `api/SubjectMaster/GetChaptersBySubjectId?subjectId=${subjectId}&vendorId=${vendorId}&branchId=${branchId}`
      );

      const raw = unwrap(res);

      // ✅ ONLY this key (no extra fallback)
      const list = raw?.chapterTopicList || [];

      const opts = (Array.isArray(list) ? list : [])
        .map((x) => ({ value: x?.id, label: x?.text }))
        .filter((o) => o?.value && String(o.label || '').trim().length > 0);

      setChapterOptions(opts);
    } catch (e) {
      console.log(e);
      setChapterOptions([]);
      setTopicOptions([]);
      toast.error('Chapter list load nahi ho paayi');
    }
  };

  // ✅ TOPIC API (uses chapterTopicList => [{id,text}])
  const GetTopicsByChapterId_ById = async (chapterId) => {
    try {
      if (!chapterId) {
        setTopicOptions([]);
        return;
      }

      const vendorId = selectionData.vendor_id || 0;
      const branchId = selectionData.branch_id || 0;

      const res = await apiService.get(
        `api/SubjectMaster/GetTopicsByChapterId?chapterId=${chapterId}&vendorId=${vendorId}&branchId=${branchId}`
      );

      const raw = unwrap(res);

      // ✅ ONLY this key (no extra fallback)
      const list = raw?.chapterTopicList || [];

      const opts = (Array.isArray(list) ? list : [])
        .map((x) => ({ value: x?.id, label: x?.text }))
        .filter((o) => o?.value && String(o.label || '').trim().length > 0);

      setTopicOptions(opts);
    } catch (e) {
      console.log(e);
      setTopicOptions([]);
      toast.error('Topic list load nahi ho paayi');
    }
  };

  // ✅ Load GetById for edit (Data1)
  const LoadTeachingById = async (id) => {
    try {
      setLoading(true);
      hydratingRef.current = true;

      const vendorId = selectionData.vendor_id || 0;
      const branchId = selectionData.branch_id || 0;

      const res = await apiService.get(
        `api/TeachingMaster/GetTeachingById?id=${id}&vendorId=${vendorId}&branchId=${branchId}`
      );
      const raw = unwrap(res);

      if (
        raw?.status !== true ||
        !Array.isArray(raw?.Data1) ||
        raw.Data1.length === 0
      ) {
        alert(raw?.Message || 'Record not found');
        return;
      }

      const row = raw.Data1[0];

      const subjectId = row.subject_id ?? '';
      const chapterId = row.subject_chapter_id ?? '';
      const topicId = row.subject_topic_id ?? '';

      setTeachingData((p) => ({
        ...p,
        id: row.id ?? id,

        vendor_id: row.vendor_id ?? vendorId,
        branch_id: row.branch_id ?? branchId,

        class_id: row.class_id ?? '',
        batch_id: row.batch_id ?? '',
        faculty_id: row.faculty_id ?? '',

        subject_id: subjectId,
        chapter_id: chapterId,
        topic_id: topicId,

        date: row.date ? String(row.date).substring(0, 10) : p.date,
        timetable_id: row.timetable_id ?? '',
        topic_covered: row.topic_covered ?? '',
        homework: row.homework ?? '',
      }));

      await GetChaptersBySubjectId(subjectId);
      if (chapterId) await GetTopicsByChapterId_ById(chapterId);
    } catch (e) {
      console.log(e);
      alert('Error loading record');
    } finally {
      hydratingRef.current = false;
      setLoading(false);
    }
  };

  // ✅ Prefill from location.state
  useEffect(() => {
    if (!location.state) return;

    const st = location.state;
    const vId = st?.vendor_id ?? user?.vendor_id ?? 0;
    const bId = st?.branch_id ?? user?.branch_id ?? 0;

    setSelectionData({ vendor_id: vId, branch_id: bId });

    setBatchName(st?.batch?.batch_name || st?.batch_name || '');
    setSubjectName(st?.batch?.subject_name || st?.subject_name || '');

    setTeachingData((prev) => ({
      ...prev,
      id: st?.id ?? prev.id ?? 0,

      vendor_id: vId,
      branch_id: bId,

      class_id: st?.batch?.class_id ?? st?.class_id ?? prev.class_id ?? '',
      batch_id: st?.batch?.id ?? st?.batch_id ?? prev.batch_id ?? '',

      faculty_id: st?.faculty_id ?? prev.faculty_id ?? '',
      subject_id:
        st?.batch?.subject_id ?? st?.subject_id ?? prev.subject_id ?? '',

      chapter_id:
        st?.subject_chapter_id ?? st?.chapter_id ?? prev.chapter_id ?? '',
      topic_id: st?.subject_topic_id ?? st?.topic_id ?? prev.topic_id ?? '',

      date: st?.date ? String(st.date).substring(0, 10) : prev.date,
      timetable_id: st?.timetable_id ?? prev.timetable_id ?? '',
      topic_covered: st?.topic_covered ?? '',
      homework: st?.homework ?? '',
    }));

    // If edit and only id given
    if (st?.id && !st?.batch && !st?.topic_covered && !st?.homework) {
      LoadTeachingById(st.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, user]);

  // ✅ subject change => load chapters
  useEffect(() => {
    if (hydratingRef.current) return;

    if (!teachingData.subject_id) {
      setChapterOptions([]);
      setTopicOptions([]);
      return;
    }

    GetChaptersBySubjectId(teachingData.subject_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    teachingData.subject_id,
    selectionData.vendor_id,
    selectionData.branch_id,
  ]);

  // ✅ chapter change => load topics
  useEffect(() => {
    if (hydratingRef.current) return;

    if (!teachingData.chapter_id) {
      setTopicOptions([]);
      return;
    }

    GetTopicsByChapterId_ById(teachingData.chapter_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    teachingData.chapter_id,
    selectionData.vendor_id,
    selectionData.branch_id,
  ]);

  // ✅ validation
  const validateInputs = () => {
    const errors = {};

    if (
      isSuperAdmin &&
      (!selectionData.vendor_id || Number(selectionData.vendor_id) <= 0)
    ) {
      toast.error('Please select Vendor');
      errors.vendor_id = 'Vendor required';
    }

    if (
      (isAdmin || hasMultiBranch || hasSingleBranch) &&
      (!selectionData.branch_id || Number(selectionData.branch_id) <= 0)
    ) {
      toast.error('Please select Branch');
      errors.branch_id = 'Branch required';
    }

    if (!teachingData.date) {
      toast.error('Please select Date');
      errors.date = 'Date required';
    }

    setErrorMessage(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ SAVE (FIXED: sends numbers/null)
  const SaveTeaching = async () => {
    if (!validateInputs()) return;

    try {
      setLoading(true);

      const payload = {
        // ✅ IMPORTANT: numeric conversions (fixes $.class_id error)
        id: toNumOrNull(teachingData.id) ?? 0,

        vendor_id: toNumOrNull(selectionData.vendor_id),
        branch_id: toNumOrNull(selectionData.branch_id),

        class_id: toNumOrNull(teachingData.class_id),
        batch_id: toNumOrNull(teachingData.batch_id),
        faculty_id: toNumOrNull(teachingData.faculty_id),
        subject_id: toNumOrNull(teachingData.subject_id),
        timetable_id: toNumOrNull(teachingData.timetable_id),

        subject_chapter_id: toNumOrNull(teachingData.chapter_id),
        subject_topic_id: toNumOrNull(teachingData.topic_id),

        date: teachingData.date || null,
        topic_covered: teachingData.topic_covered || '',
        homework: teachingData.homework || '',
      };

      console.log('SAVE PAYLOAD =>', payload);

      let res;
      if (Number(teachingData.id || 0) > 0) {
        res = await apiService.put(
          `api/TeachingMaster/UpdateTeachingMaster?id=${teachingData.id}`,
          payload
        );
      } else {
        res = await apiService.post(
          `api/TeachingMaster/AddTeachingMaster`,
          payload
        );
      }
      if (res.status === true) {
        toast.success(res.message || 'Saved Successfully');
        navigate('/list-teaching');
      } else {
        toast.error(res.message || 'Failed');
      }
    } catch (e) {
      console.log(e);
      toast.error('Something went wrong.');

      setLoading(false);
    }
  };

  const selectedChapter =
    chapterOptions.find(
      (o) => Number(o.value) === Number(teachingData.chapter_id)
    ) || null;

  const selectedTopic =
    topicOptions.find(
      (o) => Number(o.value) === Number(teachingData.topic_id)
    ) || null;

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='shadow-sm border-0 mb-4'>
        {/* Header */}
        <CardTitle
          tag='div'
          className='px-4 py-3 border-bottom d-flex align-items-center justify-content-between'
        >
          <div>
            <h6 className='mb-1 fw-semibold'>
              {teachingData.id > 0
                ? 'Edit Teaching Record'
                : 'Add Teaching Record'}
            </h6>

            {!teachingData.id && (
              <small className='text-muted d-block'>
                Batch: <strong>{batchName}</strong> &nbsp;|&nbsp; Subject:{' '}
                <strong>{subjectName}</strong>
              </small>
            )}
          </div>

          <Button
            color='warning'
            className='border'
            type='button'
            onClick={() => navigate(-1)}
          >
            <i className='bi bi-arrow-left me-1' />
            Back
          </Button>
        </CardTitle>

        {/* Body */}
        <CardBody className='px-4 py-3'>
          <Form>
            <Row className='g-3'>
              {/* Date */}
              <FormGroup className='col-md-4'>
                <Label className='form-label fw-medium'>Date</Label>
                <Input
                  type='date'
                  value={teachingData.date}
                  onChange={(e) =>
                    setTeachingData((p) => ({ ...p, date: e.target.value }))
                  }
                />
                {errorMessage.date && (
                  <FormText color='danger'>{errorMessage.date}</FormText>
                )}
              </FormGroup>

              {/* Chapter */}
              <FormGroup className='col-md-4'>
                <Label className='form-label fw-medium'>Chapter</Label>
                <Select
                  options={chapterOptions}
                  value={selectedChapter}
                  onChange={(opt) => {
                    setTeachingData((prev) => ({
                      ...prev,
                      chapter_id: opt ? opt.value : '',
                      topic_id: '',
                    }));
                  }}
                  isSearchable
                  isClearable
                  placeholder='Select chapter'
                />
              </FormGroup>

              {/* Topic */}
              <FormGroup className='col-md-4'>
                <Label className='form-label fw-medium'>Topic</Label>
                <Select
                  options={topicOptions}
                  value={selectedTopic}
                  onChange={(opt) => {
                    setTeachingData((prev) => ({
                      ...prev,
                      topic_id: opt ? opt.value : '',
                    }));
                  }}
                  isSearchable
                  isClearable
                  isDisabled={!teachingData.chapter_id}
                  placeholder={
                    teachingData.chapter_id
                      ? 'Select topic'
                      : 'Select chapter first'
                  }
                />
              </FormGroup>

              {/* Topic Covered */}
              <FormGroup className='col-12'>
                <Label className='form-label fw-medium'>
                  Describe What's Covered in today's class
                </Label>
                <Input
                  type='textarea'
                  rows={2}
                  placeholder='Briefly describe what was covered in class'
                  value={teachingData.topic_covered}
                  onChange={(e) =>
                    setTeachingData((p) => ({
                      ...p,
                      topic_covered: e.target.value,
                    }))
                  }
                />
              </FormGroup>

              {/* Homework */}
              <FormGroup className='col-12'>
                <Label className='form-label fw-medium'>
                  Any Homework / Remarks given
                </Label>
                <Input
                  type='textarea'
                  rows={2}
                  placeholder='Enter homework or assignments given'
                  value={teachingData.homework}
                  onChange={(e) =>
                    setTeachingData((p) => ({ ...p, homework: e.target.value }))
                  }
                />
              </FormGroup>
            </Row>
          </Form>
        </CardBody>

        {/* Footer Actions */}
        <div className='px-4 py-3 border-top d-flex justify-content-end gap-2'>
          <Button
            color='secondary'
            outline
            type='button'
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>

          <Button
            color='primary'
            type='button'
            className='ml-2'
            disabled={loading}
            onClick={SaveTeaching}
          >
            {loading && (
              <span className='spinner-border spinner-border-sm me-2' />
            )}
            {loading ? 'Saving...' : 'Save Record'}
          </Button>
        </div>
      </Card>
    </>
  );
};

export default Teaching_Master_Add_Edit_Screen;
